import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { clearPagedListCache } from '../hooks/usePagedList';

/** Extra per-request cache control on top of the axios config. */
export type CacheableRequestConfig = AxiosRequestConfig & {
  bypassCache?: boolean;
};

// Configure Backend API URL
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.pokebinder.net';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Ensures cookies are sent with the request
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Don't redirect on 401 errors - let the app handle authentication state
    // The UserContext will handle showing login buttons when needed
    return Promise.reject(error);
  }
);

// --- API Functions ---

// Fetch All Expansions (Sets). Cached per lang (5min TTL + in-flight
// sharing): card-details + set-details each did fetchSetsV2 then
// fetchSetCardsV2 sequentially (I18) — navigating set→card→set re-downloaded
// the whole list every time.
const setsCache = new Map<string, { at: number; data: unknown }>();
const setsInflight = new Map<string, Promise<unknown>>();
const SETS_TTL_MS = 5 * 60_000;
export const fetchSetsV2 = async (lang: 'E' | 'J' | 'C' = 'E', options: CacheableRequestConfig = {}) => {
  const { bypassCache, ...axiosOptions } = options;
  const cached = setsCache.get(lang);
  if (!bypassCache && cached && Date.now() - cached.at < SETS_TTL_MS) {
    return cached.data;
  }
  const inflight = setsInflight.get(lang);
  if (!bypassCache && inflight) {
    return inflight;
  }
  const request = (async () => {
    try {
      const response = await api.get('/sets', { params: { lang }, ...axiosOptions });
      setsCache.set(lang, { at: Date.now(), data: response.data });
      return response.data;
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error fetching v2 sets:', error);
      }
      throw error;
    } finally {
      setsInflight.delete(lang);
    }
  })();
  if (!bypassCache) {
    setsInflight.set(lang, request);
  }
  return request;
};

// Fetch Series
export const fetchSeries = async (options = {}) => {
  try {
    const response = await api.get('/series', options);
    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error('Error fetching series:', error);
    }
    throw error;
  }
};

// Fetch Cards by Set ID. Cached per (setId, lang) for 5min with in-flight
// sharing so set page <-> card page navigation doesn't refetch the whole set.
// Requests that carry their own axios options (e.g. an abort signal) skip it.
const setCardsCache = new Map<string, { at: number; data: unknown }>();
const setCardsInflight = new Map<string, Promise<unknown>>();
const SET_CARDS_TTL_MS = 5 * 60_000;
export const fetchSetCardsV2 = async (setId: string, lang: 'E' | 'J' | 'C' = 'E', options: AxiosRequestConfig = {}) => {
  if (!setId) {
    console.error('fetchSetCardsV2 requires a set ID.');
    return [];
  }
  const key = `${setId}|${lang}`;
  const useCache = Object.keys(options).length === 0;
  if (useCache) {
    const cached = setCardsCache.get(key);
    if (cached && Date.now() - cached.at < SET_CARDS_TTL_MS) return cached.data;
    const inflight = setCardsInflight.get(key);
    if (inflight) return inflight;
  }
  const request = (async () => {
    try {
      const response = await api.get('/cards', {
        params: { setID: setId, lang },
        ...options,
      });
      if (useCache) setCardsCache.set(key, { at: Date.now(), data: response.data });
      return response.data;
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error(`Error fetching v2 cards for set ID "${setId}":`, error);
      }
      throw error;
    } finally {
      setCardsInflight.delete(key);
    }
  })();
  if (useCache) setCardsInflight.set(key, request);
  return request;
};

// Fetch just one card number's variants within a set (exact cardNumber match).
export const fetchSetCardVariants = async (
  setId: string,
  number: string,
  lang: 'E' | 'J' | 'C' = 'E',
  options: AxiosRequestConfig = {}
) => {
  try {
    const response = await api.get('/cards', {
      params: { setID: setId, number, lang },
      ...options,
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error(`Error fetching card "${number}" for set ID "${setId}":`, error);
    }
    throw error;
  }
};

// Fetch User Profile
export const fetchProfile = async (options = {}) => {
  try {
    const response = await api.get('/user/me', options);
    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error('Error fetching v2 profile:', error);
    }
    throw error;
  }
};

// Fetch Home data. Pass a lang to filter to one language (no lang =
// legacy mixed-language response, unchanged for existing callers).
export const fetchHome = async (
  lang?: 'E' | 'J' | 'C' | CacheableRequestConfig,
  options: CacheableRequestConfig = {}
) => {
  // Back-compat: fetchHome(options) still works.
  if (lang !== undefined && typeof lang !== 'string') {
    options = lang ?? {};
    lang = undefined;
  }
  try {
    const response = await api.get('/home', { params: lang ? { lang } : {}, ...options });
    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error('Error fetching home data:', error);
    }
    throw error;
  }
};

// Fetch Collection Summary
export const fetchCollectionSummary = async (options = {}) => {
  try {
    const response = await api.get('/collection', options);
    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error('Error fetching collection summary:', error);
    }
    throw error;
  }
};

// Daily value of the user's collection: one recorded close per past day plus a
// live value for today. Dates arrive as UTC midnights of calendar days; they are
// rebuilt as local midnights so the chart labels the same day, not the day before.
export const fetchCollectionValueHistory = async (days = 365): Promise<PriceHistoryEntry[]> => {
  const response = await api.get<{ date: string; tcgPlayerPrice: number }[]>('/collection/pricehistory', {
    params: { days },
  });
  return (response.data ?? []).map((r) => {
    const [y, m, d] = r.date.slice(0, 10).split('-').map(Number);
    return { date: new Date(y, m - 1, d).getTime(), prices: { tcgPlayer: r.tcgPlayerPrice } };
  });
};

// Fetch Collection Details (all cards in collection).
// In-flight dedup + 15s TTL: card-details + set-details + search mounted
// together used to fire 3× full GET /collection/cards (I17). Pass
// { bypassCache: true } (as an axios-unrelated option, stripped below) to
// force a fresh read after a mutation.
export interface CollectionCard {
  id: string;
  name: string;
  cardNumber: string;
  quantity: number;
  printedTotal: number;
  setId?: string;
  [key: string]: unknown;
}

let collectionDetailsInflight: Promise<CollectionCard[]> | null = null;
let collectionDetailsCache: { at: number; data: CollectionCard[] } | null = null;
const COLLECTION_DETAILS_TTL_MS = 15_000;
export const fetchCollectionDetails = async (options: CacheableRequestConfig = {}) => {
  const { bypassCache, params, ...axiosOptions } = options;
  const hasParams = params !== undefined && Object.keys(params).length > 0;
  if (!bypassCache && !hasParams && collectionDetailsCache && Date.now() - collectionDetailsCache.at < COLLECTION_DETAILS_TTL_MS) {
    return collectionDetailsCache.data;
  }
  if (!bypassCache && !hasParams && collectionDetailsInflight) {
    return collectionDetailsInflight;
  }
  const request = (async () => {
    try {
      const response = await api.get('/collection/cards', { params, ...axiosOptions });
      const raw: unknown = response.data;
      const normalizedData: CollectionCard[] = Array.isArray(raw) ? (raw as CollectionCard[]) : [];
      if (!hasParams) {
        collectionDetailsCache = { at: Date.now(), data: normalizedData };
      }
      return normalizedData;
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error fetching collection details:', error);
      }
      throw error;
    } finally {
      if (!hasParams) {
        collectionDetailsInflight = null;
      }
    }
  })();
  if (!bypassCache && !hasParams) {
    collectionDetailsInflight = request;
  }
  return request;
};

export interface CollectionPageQuery {
  q?: string;
  setID?: string;
  sort?: 'value' | 'name' | 'quantity' | 'number' | 'set';
  dir?: 'asc' | 'desc';
}

// One page of the collection, filtered and sorted server-side. total is the
// number of rows matching the filters (X-Total-Count), not the page size.
export const fetchCollectionPage = async (
  query: CollectionPageQuery,
  offset: number,
  limit: number,
  signal?: AbortSignal
): Promise<{ rows: CollectionCard[]; total: number }> => {
  const params = { ...query, q: query.q || undefined, setID: query.setID || undefined, offset, limit };
  const response = await api.get('/collection/cards', { params, signal });
  const rows: CollectionCard[] = Array.isArray(response.data) ? response.data : [];
  return { rows, total: Number(response.headers['x-total-count'] ?? rows.length) };
};

/** Drop the collection-details cache (call after PUT /collection/card). */
export const invalidateCollectionDetailsCache = () => {
  collectionDetailsCache = null;
  clearPagedListCache();
};

// Add Card to Collection
export const addCardToCollection = async (cardId: string, quantity: number, options = {}) => {
  if (!cardId) {
    console.error('addCardToCollection requires cardId.');
    throw new Error('Missing card ID for adding card.');
  }
  if (!quantity || quantity <= 0) {
    console.error('addCardToCollection requires a positive quantity.');
    throw new Error('Missing or invalid quantity for adding card.');
  }
  
  try {
    const response = await api.put(
      '/collection/card',
      {
        id: cardId,
        quantity,
      },
      options
    );
    // Quantity changed — drop the cached collection so the caller's refetch
    // reads fresh data instead of the pre-mutation snapshot (K2).
    invalidateCollectionDetailsCache();
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return { error: true, status: 401, message: 'Unauthorized, redirecting to login.' };
    }
    if (!axios.isCancel(error)) {
      console.error(`Error adding card "${cardId}" to collection:`, error);
    }
    throw error;
  }
};

// Remove Card from Collection  
export const removeCardFromCollection = async (cardId: string, quantity: number, options = {}) => {
  if (!cardId) {
    console.error('removeCardFromCollection requires cardId.');
    throw new Error('Missing card ID for removing card.');
  }
  if (!quantity || quantity <= 0) {
    console.error('removeCardFromCollection requires a positive quantity.');
    throw new Error('Missing or invalid quantity for removing card.');
  }
  
  try {
    const response = await api.put(
      '/collection/card',
      {
        id: cardId,
        quantity: -quantity, // Negative quantity to remove
      },
      options
    );
    // Quantity changed — drop the cached collection so the caller's refetch
    // reads fresh data instead of the pre-mutation snapshot (K2).
    invalidateCollectionDetailsCache();
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return { error: true, status: 401, message: 'Unauthorized, redirecting to login.' };
    }
    if (!axios.isCancel(error)) {
      console.error(`Error removing card "${cardId}" from collection:`, error);
    }
    throw error;
  }
};

// Fetch the user's favourite cards. A non-array payload (an error envelope
// on a soft failure) reads as "no favourites" rather than crashing the caller.
// Which of these printings are favourites? Bounded by the ids asked about,
// unlike fetchFavouriteCards, which returns the whole list.
export const fetchFavouriteIds = async (cardIds: string[]): Promise<Set<string>> => {
  if (cardIds.length === 0) return new Set();
  const response = await api.get('/favourites/cards', { params: { cardIDs: cardIds.join(',') } });
  const rows: { id?: string }[] = Array.isArray(response.data) ? response.data : [];
  return new Set(rows.map((r) => r.id).filter((id): id is string => !!id));
};

export const fetchFavouriteCards = async (options = {}) => {
  try {
    const response = await api.get('/favourites/cards', options);
    const raw: unknown = response.data;
    return Array.isArray(raw) ? raw : [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return { error: true, status: 401, message: 'Unauthorized, redirecting to login.' };
    }
    if (!axios.isCancel(error)) {
      console.error('Error fetching favourite cards:', error);
    }
    throw error;
  }
};

// Add Card to Favourites
export const addFavouriteCard = async (cardId: string, options = {}) => {
  if (!cardId) {
    console.error('addFavouriteCard requires cardId.');
    throw new Error('Missing card ID for adding favourite.');
  }

  try {
    const response = await api.put(
      '/favourites/card',
      {
        id: cardId,
      },
      options
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return { error: true, status: 401, message: 'Unauthorized, redirecting to login.' };
    }
    if (!axios.isCancel(error)) {
      console.error(`Error adding card "${cardId}" to favourites:`, error);
    }
    throw error;
  }
};

// Remove Card from Favourites
export const removeFavouriteCard = async (cardId: string, options = {}) => {
  if (!cardId) {
    console.error('removeFavouriteCard requires cardId.');
    throw new Error('Missing card ID for removing favourite.');
  }

  try {
    // DELETE carries the card id in a JSON body (not the URL).
    const response = await api.delete('/favourites/card', {
      data: { id: cardId },
      ...options,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return { error: true, status: 401, message: 'Unauthorized, redirecting to login.' };
    }
    if (!axios.isCancel(error)) {
      console.error(`Error removing card "${cardId}" from favourites:`, error);
    }
    throw error;
  }
};

// Fetch the user's want cards. A non-array payload (an error envelope
// on a soft failure) reads as "no wants" rather than crashing the caller.
// Which of these printings are wants? Bounded by the ids asked about,
// unlike fetchWantCards, which returns the whole list.
export const fetchWantIds = async (cardIds: string[]): Promise<Set<string>> => {
  if (cardIds.length === 0) return new Set();
  const response = await api.get('/wants/cards', { params: { cardIDs: cardIds.join(',') } });
  const rows: { id?: string }[] = Array.isArray(response.data) ? response.data : [];
  return new Set(rows.map((r) => r.id).filter((id): id is string => !!id));
};

export interface WantCard {
  id: string;
  name: string;
  cardNumber: string;
  series?: string;
  setName?: string;
  lang?: string;
  images?: { cardSmall?: string };
  prices?: { tcgPlayer?: number };
}

export const fetchWantPage = async (
  offset: number,
  limit: number,
  signal?: AbortSignal
): Promise<{ rows: WantCard[]; total: number }> => {
  const response = await api.get('/wants/cards', { params: { offset, limit }, signal });
  const rows: WantCard[] = Array.isArray(response.data) ? response.data : [];
  return { rows, total: Number(response.headers['x-total-count'] ?? rows.length) };
};

export const fetchWantCards = async (options = {}) => {
  try {
    const response = await api.get('/wants/cards', options);
    const raw: unknown = response.data;
    return Array.isArray(raw) ? raw : [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return { error: true, status: 401, message: 'Unauthorized, redirecting to login.' };
    }
    if (!axios.isCancel(error)) {
      console.error('Error fetching want cards:', error);
    }
    throw error;
  }
};

// Add Card to Wants
export const addWantCard = async (cardId: string, options = {}) => {
  if (!cardId) {
    console.error('addWantCard requires cardId.');
    throw new Error('Missing card ID for adding want.');
  }

  try {
    const response = await api.put(
      '/wants/card',
      {
        id: cardId,
      },
      options
    );
    clearPagedListCache();
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return { error: true, status: 401, message: 'Unauthorized, redirecting to login.' };
    }
    if (!axios.isCancel(error)) {
      console.error(`Error adding card "${cardId}" to wants:`, error);
    }
    throw error;
  }
};

// Remove Card from Wants
export const removeWantCard = async (cardId: string, options = {}) => {
  if (!cardId) {
    console.error('removeWantCard requires cardId.');
    throw new Error('Missing card ID for removing want.');
  }

  try {
    // DELETE carries the card id in a JSON body (not the URL).
    const response = await api.delete('/wants/card', {
      data: { id: cardId },
      ...options,
    });
    clearPagedListCache();
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return { error: true, status: 401, message: 'Unauthorized, redirecting to login.' };
    }
    if (!axios.isCancel(error)) {
      console.error(`Error removing card "${cardId}" from wants:`, error);
    }
    throw error;
  }
};

// Search for cards and sets. lang is explicit (default E) so callers can
// reach J/C cards — previously only `q` was sent and the backend always
// filtered English (J2).
export const search = async (query: string, lang: 'E' | 'J' | 'C' = 'E', options = {}) => {
  if (!query) {
    console.error('search requires a query.');
    return { cards: [], sets: [], truncated: false };
  }
  try {
    const response = await api.get('/search', {
      params: { q: query, lang },
      ...options,
    });
    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error(`Error searching "${query}":`, error);
    }
    throw error;
  }
};

export interface SearchPageQuery {
  q: string;
  lang?: 'E' | 'J' | 'C';
  /** cards = everything but sealed products; products = card number 000. */
  kind?: 'cards' | 'products';
  sort?: 'relevance' | 'name' | 'price' | 'release' | 'rarity';
  dir?: 'asc' | 'desc';
  /** Filters, comma-joined on the wire. Facets always describe the unfiltered matches. */
  rarity?: string[];
  setID?: string[];
  minPrice?: number;
  maxPrice?: number;
}

export interface SearchFacet {
  value: string;
  label?: string;
  count: number;
}

// One page of card matches in a stable server-side order. The first page
// (offset 0, not kind=products) also carries the set matches.
export const searchPage = async <C = unknown, S = unknown>(
  query: SearchPageQuery,
  offset: number,
  limit: number,
  signal?: AbortSignal
): Promise<{
  rows: C[];
  total: number;
  meta: { sets: S[]; setsTruncated: boolean; facets?: { rarities: SearchFacet[]; sets: SearchFacet[] } };
}> => {
  const response = await api.get('/search', {
    params: {
      ...query,
      rarity: query.rarity?.length ? query.rarity.join(',') : undefined,
      setID: query.setID?.length ? query.setID.join(',') : undefined,
      lang: query.lang ?? 'E',
      offset,
      limit,
    },
    signal,
  });
  const data = response.data ?? {};
  return {
    rows: Array.isArray(data.cards) ? data.cards : [],
    total: Number(data.total ?? 0),
    meta: {
      sets: Array.isArray(data.sets) ? data.sets : [],
      setsTruncated: data.truncated === true,
      facets: data.facets,
    },
  };
};

// Generate a long-lived API token for MCP / external integrations
export const generateApiToken = async (): Promise<{ token: string }> => {
  try {
    const response = await api.get('/user/api-token');
    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error('Error generating API token:', error);
    }
    throw error;
  }
};

// Delete user profile/account
export const deleteProfile = async () => {
  try {
    const response = await api.delete('/user/');
    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error('Error deleting profile:', error);
    }
    throw error;
  }
};

// Price History Types
export interface PriceHistoryEntry {
  date: number; // timestamp in milliseconds
  prices: {
    tcgPlayer: number;
  };
}

// Fetch Price History for a card. Cached per card (5min TTL + in-flight
// sharing): compare-overlay refetches both columns on every open and
// card-details refetches on every variant toggle (I20).
const priceHistoryCache = new Map<string, { at: number; data: PriceHistoryEntry[] }>();
const priceHistoryInflight = new Map<string, Promise<PriceHistoryEntry[]>>();
const PRICE_HISTORY_TTL_MS = 5 * 60_000;
export const fetchPriceHistory = async (cardId: string, options: CacheableRequestConfig = {}): Promise<PriceHistoryEntry[]> => {
  if (!cardId) {
    console.error('fetchPriceHistory requires a card ID.');
    return [];
  }
  const { bypassCache, ...axiosOptions } = options;
  const cached = priceHistoryCache.get(cardId);
  if (!bypassCache && cached && Date.now() - cached.at < PRICE_HISTORY_TTL_MS) {
    return cached.data;
  }
  const inflight = priceHistoryInflight.get(cardId);
  if (!bypassCache && inflight) {
    return inflight;
  }
  const request = (async () => {
    try {
      const response = await api.get('/card/pricehistory', { params: { id: cardId }, ...axiosOptions });
      const data = response.data || [];
      priceHistoryCache.set(cardId, { at: Date.now(), data });
      return data;
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error(`Error fetching price history for card "${cardId}":`, error);
      }
      throw error;
    } finally {
      priceHistoryInflight.delete(cardId);
    }
  })();
  if (!bypassCache) {
    priceHistoryInflight.set(cardId, request);
  }
  return request;
};

export default api;
